import { Box } from '@mui/material';
import { type ReactFlowState, useStore, type XYPosition } from '@xyflow/react';
import { isEqual } from 'lodash';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLatest } from 'react-use';

import { LINE_HANDLE_SELECTOR } from './LineControlPoint';

/**
 * The space between a selection toolbar and the node, label, or handle below
 * it, in screen pixels. The node and line toolbars use the same space.
 */
export const SELECTION_TOOLBAR_OFFSET = 12;
/**
 * The toolbar is in the React Flow renderer, like `NodeToolbar`. The viewport
 * that draws the nodes and lines is at z-index 2 in the renderer, so the
 * toolbar is above every node and line.
 */
const TOOLBAR_Z_INDEX = 3;
/** React Flow does not pan, zoom, or drag the canvas from the toolbar. */
const NO_CANVAS_GESTURE = 'nodrag nopan nowheel';

/** A box on the screen, in screen pixels. */
export type ScreenBox = Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>;

const rendererSelector = (state: ReactFlowState) =>
  state.domNode?.querySelector<HTMLElement>('.react-flow__renderer') ?? null;

/**
 * Returns the bottom center of the line toolbar, in screen pixels. The
 * toolbar is centered above the labels, and `offset` above the highest one.
 * If a handle is in the area of the toolbar, the toolbar moves above that
 * handle, so that the user can see and drag every handle. A line without a
 * label has a label box of zero size at its label point.
 */
export function getLineToolbarAnchor(
  labels: ScreenBox[],
  handles: ScreenBox[],
  toolbar: { width: number; height: number },
  offset: number
): XYPosition | null {
  if (labels.length === 0) return null;
  const x =
    (Math.min(...labels.map((label) => label.left)) +
      Math.max(...labels.map((label) => label.right))) /
    2;
  let y = Math.min(...labels.map((label) => label.top)) - offset;
  // From the lowest handle up: a handle can overlap the toolbar only after
  // the toolbar moved above all the handles below it.
  const byHeight = [...handles].sort((a, b) => b.top - a.top);
  for (const handle of byHeight) {
    const overlaps =
      handle.right > x - toolbar.width / 2 &&
      handle.left < x + toolbar.width / 2 &&
      handle.bottom > y - toolbar.height &&
      handle.top < y;
    if (overlaps) y = handle.top - offset;
  }
  return { x, y };
}

/**
 * Shows `children` above the labels of the selected lines. React Flow has
 * no edge toolbar, so this works like its `NodeToolbar`: the toolbar is in
 * the renderer, outside the zoomed viewport, so it keeps its screen size at
 * every zoom level.
 *
 * The drawn label sets the position, because the line component computes the
 * label point and the user can drag the label or edit its text. The toolbar
 * measures the labels and handles at each animation frame while it shows,
 * so that it follows pans, zooms, label drags, and node moves.
 */
export function DiagramLineToolbar({
  edgeIDs,
  children,
}: PropsWithChildren<{ edgeIDs: string[] }>) {
  const renderer = useStore(rendererSelector);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const latestEdgeIDs = useLatest(edgeIDs);
  const [anchor, setAnchor] = useState<XYPosition | null>(null);

  useEffect(() => {
    if (!renderer) return;
    let frame = 0;
    const follow = () => {
      const toolbar = toolbarRef.current;
      if (toolbar) {
        const origin = renderer.getBoundingClientRect();
        const labels = latestEdgeIDs.current.flatMap((id) => {
          const label = renderer.querySelector(
            `[data-edge-label-id="${CSS.escape(id)}"]`
          );
          return label ? [label.getBoundingClientRect()] : [];
        });
        const handles = Array.from(
          renderer.querySelectorAll(LINE_HANDLE_SELECTOR),
          (handle) => handle.getBoundingClientRect()
        );
        const screen = getLineToolbarAnchor(
          labels,
          handles,
          { width: toolbar.offsetWidth, height: toolbar.offsetHeight },
          SELECTION_TOOLBAR_OFFSET
        );
        const next = screen && {
          x: screen.x - origin.left,
          y: screen.y - origin.top,
        };
        setAnchor((previous) => (isEqual(previous, next) ? previous : next));
      }
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [renderer, latestEdgeIDs]);

  if (!renderer) return null;
  return createPortal(
    <Box
      ref={toolbarRef}
      className={NO_CANVAS_GESTURE}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: TOOLBAR_Z_INDEX,
        // The toolbar shows after the first measure, at its anchor.
        visibility: anchor ? 'visible' : 'hidden',
        transform: anchor
          ? `translate(${anchor.x}px, ${anchor.y}px) translate(-50%, -100%)`
          : undefined,
      }}
    >
      {children}
    </Box>,
    renderer
  );
}
